import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowDownTrayIcon, ArrowLeftIcon, BookOpenIcon, ChatBubbleLeftRightIcon, DocumentTextIcon, ScaleIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import SlideDrawer from "../common/SlideDrawer";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { revealProps } from "../common/Reveal";
import PdfFileViewer, { PdfHighlight, PdfViewerHandle } from "../Investigations/viewers/PdfFileViewer";
import TextFileViewer, { TextViewerHandle } from "../Investigations/viewers/TextFileViewer";
import { TagChip } from "../Agency/AgencyDocuments/tagColors";
import { POLICY_DOC_TYPE_LABELS, PolicyDocumentDetail as DetailData, PolicySection, policiesApi } from "./api";
import PolicyChat from "./PolicyChat";

const TYPE_ICON: Record<string, React.FC<{ className?: string }>> = { policy: BookOpenIcon, statute: ScaleIcon, other: DocumentTextIcon };

// One document for an officer: the PDF (or extracted text) on the left and a
// sections outline on the right. ?page= lands the viewer, ?section= (from
// the editor's Policies panel and chat citations) highlights the section.
const PolicyDocumentDetail: React.FC<{ agencyName: string }> = ({ agencyName }) => {
  const { documentId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<DetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>(params.get("section"));
  // Hovering an outline row outlines the section on the page (when it is on
  // the current page) and hovering the page outlines the row.
  const [hoverSection, setHoverSection] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useLocalStorageState("policies.chatOpen", false);
  const [filter, setFilter] = useState("");
  const viewerRef = useRef<PdfViewerHandle | TextViewerHandle | null>(null);
  const outline = useResizablePanel({ storageKey: "pnai-policyOutlineWidth", defaultWidth: 360, minWidth: 240, maxWidth: 560, side: "left" });
  const initialPage = Number(params.get("page")) || undefined;
  const scrolledTo = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await policiesApi.document(documentId));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.status === 404 ? "This document is no longer available." : "Could not load the document.");
    }
  }, [documentId]);
  useEffect(() => {
    load();
  }, [load]);

  // A presigned URL lives an hour; if the viewer's fetch dies, mint a fresh
  // one once instead of leaving a broken pane.
  const refreshed = useRef(false);
  const onViewerError = () => {
    if (refreshed.current) return;
    refreshed.current = true;
    load();
  };

  // Highlight and scroll to the section named in the URL once the outline
  // is in; if it has no explicit page, open its own.
  useEffect(() => {
    if (!data || !activeSection || scrolledTo.current === activeSection) return;
    scrolledTo.current = activeSection;
    const el = document.getElementById(`outline-${activeSection}`);
    el?.scrollIntoView({ block: "center" });
    if (!initialPage) {
      const s = data.sections.find((x) => x.id === activeSection);
      if (s?.pageStart) setTimeout(() => viewerRef.current?.goToPage(s.pageStart!), 80);
    }
  }, [data, activeSection, initialPage]);

  const highlights = useMemo<PdfHighlight[]>(
    () => (data ? data.sections.filter((s) => s.anchor?.boxes?.length).map((s) => ({ id: s.id, boxes: s.anchor!.boxes })) : []),
    [data]
  );

  const sections = useMemo(() => {
    if (!data) return [];
    const q = filter.trim().toLowerCase();
    return data.sections.filter((s) => !q || (s.heading || "").toLowerCase().includes(q) || (s.sectionNumber || "").toLowerCase().includes(q) || s.tags.some((t) => t.name.toLowerCase().includes(q)));
  }, [data, filter]);

  // Opened from a policy suggestion in the case editor: Back returns to the
  // report, not the Policies list.
  const fromEditor = Boolean((location.state as { fromEditor?: boolean } | null)?.fromEditor);
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/policies/documents");
  };

  const open = (s: PolicySection) => {
    setActiveSection(s.id);
    if (s.pageStart) viewerRef.current?.goToPage(s.pageStart);
  };

  // Clicking a section's box on the page selects it in the outline.
  const selectFromPage = (id: string) => {
    setActiveSection(id);
    document.getElementById(`outline-${id}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  };

  const download = async () => {
    try {
      window.open(await policiesApi.documentUrl(documentId, true), "_blank", "noopener");
    } catch {
      toast.error("Could not open the file");
    }
  };

  if (error) {
    return (
      <div className="flex-1 py-12 text-center">
        <p className="text-base-content/70">{error}</p>
        <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate("/policies/documents")}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Policies
        </Button>
      </div>
    );
  }
  if (!data) return <div className="flex-1 py-12 text-center text-sm text-base-content/60">Loading…</div>;
  const { document: doc } = data;
  const Icon = TYPE_ICON[doc.docType] || DocumentTextIcon;
  const isPdf = /\.pdf$/i.test(doc.fileName || "") && !doc.textPages;

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-4 py-3 border-b border-base-300 flex items-center gap-3 flex-wrap">
          <Button variant="secondary" size="sm" onClick={goBack} title="Back" data-tour="policy-back">
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> {fromEditor ? "Back to report" : "Policies"}
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-semibold text-base-content truncate flex items-center gap-2">
              <Icon className="size-4 text-base-content/50 shrink-0" />
              {doc.title}
            </h1>
            <p className="text-xs text-base-content/60">
              {POLICY_DOC_TYPE_LABELS[doc.docType]}
              {doc.pageCount ? ` · ${doc.pageCount} page${doc.pageCount === 1 ? "" : "s"}` : ""}
              {doc.sectionCount ? ` · ${doc.sectionCount} section${doc.sectionCount === 1 ? "" : "s"}` : ""}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={download} title="Download the original file" data-tour="policy-download">
            <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download
          </Button>
          <Button variant={chatOpen ? "primary" : "secondary"} size="sm" onClick={() => setChatOpen(!chatOpen)}>
            <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" /> Ask
          </Button>
        </div>

        <div className="flex-1 flex min-h-0">
          <div className="flex-1 min-w-0 border-r border-base-300 min-h-0">
            {isPdf ? (
              <PdfFileViewer
                ref={viewerRef as any}
                url={data.url}
                initialPage={initialPage}
                onLoadError={onViewerError}
                highlights={highlights}
                activeHighlightId={activeSection}
                hoverHighlightId={hoverSection}
                onHighlightHover={setHoverSection}
                onHighlightClick={selectFromPage}
              />
            ) : (
              <TextFileViewer ref={viewerRef as any} loadPages={() => policiesApi.pages(documentId)} initialPage={initialPage} onLoadError={onViewerError} />
            )}
          </div>
          <div style={{ width: outline.width }} className="relative shrink-0 flex min-h-0">
            <ResizeHandle edge="left" active={outline.isResizing} {...outline.handleProps} />
            <aside className="flex-1 min-w-0 overflow-auto bg-base-200/40">
              <div className="sticky top-0 z-10 bg-base-200/90 backdrop-blur px-3 py-2 border-b border-base-300">
                <input data-tour="policy-outline-filter" className="input input-bordered input-sm w-full bg-base-100 focus:outline-none focus:border-blue-500" placeholder="Find a section or topic" value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
              <ol className="p-2 space-y-0.5">
                {sections.map((s, i) => {
                  const enter = revealProps(i);
                  const active = s.id === activeSection;
                  const hovered = !active && s.id === hoverSection;
                  return (
                    <li key={s.id} id={`outline-${s.id}`} style={enter.style} className={enter.className}>
                      <button
                        data-tour="policy-outline-row"
                        onClick={() => open(s)}
                        onMouseEnter={() => setHoverSection(s.id)}
                        onMouseLeave={() => setHoverSection((cur) => (cur === s.id ? null : cur))}
                        className={cn(
                          "w-full text-left rounded-lg px-2.5 py-2 transition-colors border",
                          active
                            ? "bg-blue-50 dark:bg-blue-900/40 border-blue-200 dark:border-blue-800"
                            : hovered
                              ? "bg-base-100 border-blue-200/70 dark:border-blue-800/70"
                              : "border-transparent hover:bg-base-100"
                        )}
                      >
                        <p className="text-sm text-base-content leading-5">
                          {s.sectionNumber && <span className={cn("mr-1.5 font-medium", active ? "text-blue-600 dark:text-blue-300" : "text-base-content/70")}>{s.sectionNumber}</span>}
                          {s.heading || <span className="italic text-base-content/50">Untitled section</span>}
                        </p>
                        <p className="text-[11px] text-base-content/50">{s.pageStart != null ? `p.${s.pageStart}${s.pageEnd != null && s.pageEnd !== s.pageStart ? `-${s.pageEnd}` : ""}` : ""}</p>
                        {s.tags.length > 0 && (
                          <span className="mt-1 flex flex-wrap gap-1">
                            {s.tags.map((t) => (
                              <TagChip key={t.id} name={t.name} color={t.color} />
                            ))}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
                {sections.length === 0 && <li className="px-2 py-4 text-xs text-base-content/60">No sections match.</li>}
              </ol>
            </aside>
          </div>
        </div>
      </div>

      <SlideDrawer open={chatOpen} storageKey="pnai-policyChatWidth" className="border-l border-base-300 bg-base-100">
        <PolicyChat agencyName={agencyName} compact onClose={() => setChatOpen(false)} />
      </SlideDrawer>
    </div>
  );
};

export default PolicyDocumentDetail;
